import PropTypes from "prop-types";
import { useMemo } from "react";

import { Button } from "@transfr-inc/dashboard-components/forms";

import {
  StartOverConfirmationModal,
  useUserSelectedCareers,
} from "@features/shared";
import { buttonTracking } from "@lib/tracking.helpers";

import "./compare-career.button.scss";

const MAX_COMPARE_CAREERS = 3;

const getCompareInstructions = (selectedCareers) => {
  if (selectedCareers?.length === 0) {
    return `Add up to ${MAX_COMPARE_CAREERS} careers to compare.`;
  } else if (selectedCareers?.length < MAX_COMPARE_CAREERS) {
    const remainingCareers = MAX_COMPARE_CAREERS - selectedCareers?.length;
    const careerLabel = remainingCareers > 1 ? "careers" : "career";
    return `Find up to ${remainingCareers} more ${careerLabel}!`;
  } else {
    return `You can only compare ${MAX_COMPARE_CAREERS} careers.`;
  }
};

export const CompareCareerButton = ({ career }) => {
  const {
    selectedCareers,
    addCareer,
    removeCareer,
    removingSelectedCareer,
    addingSelectedCareer,
    fetchingSelectedCareers,
    openStartOverConfirmation,
    onStartOverCancel,
    onStartOverConfirmation,
  } = useUserSelectedCareers();

  const isLoading = useMemo(
    () =>
      removingSelectedCareer || addingSelectedCareer || fetchingSelectedCareers,
    [removingSelectedCareer, addingSelectedCareer, fetchingSelectedCareers],
  );

  const isCareerAlreadySelected = useMemo(
    () => selectedCareers?.some(({ id }) => id === career.id),
    [selectedCareers, career],
  );

  const compareInstructions = useMemo(
    () => getCompareInstructions(selectedCareers),
    [selectedCareers],
  );

  const config = useMemo(() => {
    return {
      icon: isCareerAlreadySelected
        ? ["fasr", "minus-circle"]
        : ["fasr", "plus-circle"],
      title: isCareerAlreadySelected ? "Remove from Compare" : "Compare",
      tracking: isCareerAlreadySelected ? "remove" : "add",
    };
  }, [isCareerAlreadySelected]);

  const onCompareClick = async () => {
    await (isCareerAlreadySelected ? removeCareer(career) : addCareer(career));
  };

  return (
    <>
      <div className="compare-career-button">
        <Button
          primary={!isCareerAlreadySelected}
          onClick={onCompareClick}
          icon={config.icon}
          disabled={
            isLoading ||
            (selectedCareers?.length === MAX_COMPARE_CAREERS &&
              !isCareerAlreadySelected)
          }
          loader={isLoading}
          {...buttonTracking("career-details", "compare", config.tracking)}
        >
          {config.title}
        </Button>
        <p>{compareInstructions}</p>
      </div>
      <StartOverConfirmationModal
        open={openStartOverConfirmation}
        career={career}
        onCancel={onStartOverCancel}
        onClose={onStartOverCancel}
        onConfirm={onStartOverConfirmation}
        trackingPrefix="career-details"
        isNewCareer
      />
    </>
  );
};

CompareCareerButton.propTypes = {
  career: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
};
